import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import { Check, Crown, Zap, Loader, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const Subscription = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [prices, setPrices] = useState({ monthly_price: 9.99, yearly_price: 99.99, trial_days: 7 });
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    fetchPrices();
    fetchSubscriptionStatus();
    checkPaymentStatus();
  }, []);

  const fetchPrices = async () => {
    try {
      const response = await api.get('/subscription/prices');
      setPrices(response.data);
    } catch (error) {
      console.error('Failed to fetch prices:', error);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await api.get('/payments/subscription-status');
      setSubscriptionDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    }
  };

  const checkPaymentStatus = async () => {
    const urlParams = new URLSearchParams(location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId) {
      setCheckingPayment(true);
      let attempts = 0;
      const maxAttempts = 10;

      const pollStatus = async () => {
        try {
          // First try the new subscription status endpoint
          const subResponse = await api.get('/payments/subscription-status');
          
          if (subResponse.data.has_subscription && subResponse.data.status === 'active') {
            toast.success('Payment successful! Welcome to Pro!');
            await refreshUser();
            setSubscriptionDetails(subResponse.data);
            setCheckingPayment(false);
            navigate('/subscription', { replace: true });
            return;
          }
          
          // Fallback to legacy endpoint
          const response = await api.get(`/payments/checkout/status/${sessionId}`);
          
          if (response.data.payment_status === 'paid') {
            toast.success('Payment successful! Welcome to Pro!');
            await refreshUser();
            navigate('/subscription', { replace: true });
            return;
          }

          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(pollStatus, 2000);
          } else {
            setCheckingPayment(false);
            toast.info('Payment is being processed. Please refresh in a moment.');
          }
        } catch (error) {
          console.error('Failed to check payment status:', error);
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(pollStatus, 2000);
          } else {
            setCheckingPayment(false);
          }
        }
      };

      pollStatus();
    }
  };

  const handleSubscribe = async (planType) => {
    setLoading(true);

    try {
      const originUrl = window.location.origin;
      // Use new recurring subscription endpoint
      const response = await api.post('/payments/create-subscription', {
        plan_type: planType,
        origin_url: originUrl,
      });

      window.location.href = response.data.url;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel? You will keep access until the end of your billing period.')) {
      return;
    }
    
    setCancelLoading(true);
    try {
      const response = await api.post('/payments/cancel-subscription');
      toast.success(response.data.message);
      fetchSubscriptionStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setCancelLoading(true);
    try {
      const response = await api.post('/payments/reactivate-subscription');
      toast.success(response.data.message);
      fetchSubscriptionStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 mx-auto mb-4 text-indigo-500 animate-spin" />
          <p className="text-lg text-muted-foreground">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  const isActive = user?.subscription_status === 'active' || subscriptionDetails?.has_subscription;
  const isCancelling = subscriptionDetails?.cancel_at_period_end;

  return (
    <div className="min-h-screen bg-[#030712]">
      <Navbar />

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        {/* Current Status */}
        {isActive ? (
          <div className="mb-12 p-8 bg-gradient-to-br from-indigo-500/20 via-purple-500/10 to-transparent border border-indigo-500/30 rounded-2xl backdrop-blur-xl text-center">
            <Crown className="w-16 h-16 mx-auto mb-4 text-yellow-400" />
            <h2 className="text-3xl font-bold font-['Outfit'] mb-2">You're a Pro!</h2>
            <p className="text-lg text-muted-foreground mb-4">
              Enjoy unlimited receipt scans and all premium features
            </p>
            
            {subscriptionDetails && (
              <div className="mt-6 space-y-2">
                <p className="text-sm text-muted-foreground">
                  Plan: <span className="text-white font-medium capitalize">{subscriptionDetails.plan_type}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  {isCancelling ? 'Access until:' : 'Renews on:'}{' '}
                  <span className="text-white font-medium">
                    {formatDate(subscriptionDetails.current_period_end)}
                  </span>
                </p>
                
                {isCancelling ? (
                  <div className="mt-4">
                    <p className="text-yellow-400 text-sm mb-3">
                      Your subscription will end on {formatDate(subscriptionDetails.current_period_end)}
                    </p>
                    <button
                      onClick={handleReactivateSubscription}
                      disabled={cancelLoading}
                      className="inline-flex items-center space-x-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-full text-sm font-medium transition-all disabled:opacity-50"
                    >
                      <RefreshCw className={`w-4 h-4 ${cancelLoading ? 'animate-spin' : ''}`} />
                      <span>{cancelLoading ? 'Processing...' : 'Reactivate Subscription'}</span>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelLoading}
                    className="mt-4 inline-flex items-center space-x-2 text-red-400 hover:text-red-300 text-sm transition-all disabled:opacity-50"
                  >
                    <XCircle className="w-4 h-4" />
                    <span>{cancelLoading ? 'Processing...' : 'Cancel Subscription'}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight mb-4">
              Upgrade to Pro
            </h1>
            <p className="text-lg text-muted-foreground">
              Unlock unlimited scans, advanced analytics, and more
            </p>
            <p className="text-sm text-indigo-400 mt-2">
              Auto-renews monthly or yearly • Cancel anytime
            </p>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Monthly Plan */}
          <div className="p-8 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-xl">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold mb-2">Monthly</h3>
              <div className="mb-4">
                <span className="text-5xl font-mono font-bold">
                  ${prices.monthly_price}
                </span>
                <span className="text-lg text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {prices.trial_days}-day free trial included
              </p>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-center space-x-2">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span>Unlimited receipt scans</span>
              </li>
              <li className="flex items-center space-x-2">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span>Advanced analytics & charts</span>
              </li>
              <li className="flex items-center space-x-2">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span>P&L reports & CSV export</span>
              </li>
              <li className="flex items-center space-x-2">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span>Priority support</span>
              </li>
            </ul>

            <button
              onClick={() => handleSubscribe('monthly')}
              disabled={loading || user?.subscription_status === 'active'}
              data-testid="subscribe-monthly-button"
              className="w-full bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm border border-white/10 px-6 py-3 rounded-full font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : user?.subscription_status === 'active' ? 'Current Plan' : 'Subscribe Monthly'}
            </button>
          </div>

          {/* Yearly Plan */}
          <div className="p-8 bg-gradient-to-br from-indigo-500/20 via-purple-500/10 to-transparent border border-indigo-500/30 rounded-2xl backdrop-blur-xl relative">
            <div className="absolute top-4 right-4 bg-indigo-500 text-white px-3 py-1 rounded-full text-sm font-medium">
              Save 17%
            </div>

            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold mb-2">Yearly</h3>
              <div className="mb-4">
                <span className="text-5xl font-mono font-bold">
                  ${prices.yearly_price}
                </span>
                <span className="text-lg text-muted-foreground">/year</span>
              </div>
              <p className="text-sm text-muted-foreground">
                ${(prices.yearly_price / 12).toFixed(2)}/month • {prices.trial_days}-day free trial
              </p>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-center space-x-2">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span>Everything in Monthly</span>
              </li>
              <li className="flex items-center space-x-2">
                <Zap className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <span>Save $20/year</span>
              </li>
              <li className="flex items-center space-x-2">
                <Zap className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <span>Priority feature requests</span>
              </li>
              <li className="flex items-center space-x-2">
                <Zap className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <span>Early access to new features</span>
              </li>
            </ul>

            <button
              onClick={() => handleSubscribe('yearly')}
              disabled={loading || user?.subscription_status === 'active'}
              data-testid="subscribe-yearly-button"
              className="w-full bg-white text-black hover:bg-white/90 font-medium px-6 py-3 rounded-full shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? 'Processing...' : user?.subscription_status === 'active' ? 'Current Plan' : 'Subscribe Yearly'}
            </button>
          </div>
        </div>

        {/* Features List */}
        <div className="mt-16">
          <h2 className="text-2xl font-semibold font-['Outfit'] text-center mb-8">
            Everything included in Pro
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
              <Zap className="w-8 h-8 text-indigo-400 mb-3" />
              <h3 className="font-semibold mb-2">Unlimited Scans</h3>
              <p className="text-sm text-muted-foreground">
                Scan as many receipts as you need without any limits
              </p>
            </div>
            <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
              <Zap className="w-8 h-8 text-green-400 mb-3" />
              <h3 className="font-semibold mb-2">Advanced Analytics</h3>
              <p className="text-sm text-muted-foreground">
                Interactive charts, trends, and insights into your spending
              </p>
            </div>
            <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
              <Zap className="w-8 h-8 text-cyan-400 mb-3" />
              <h3 className="font-semibold mb-2">Export Reports</h3>
              <p className="text-sm text-muted-foreground">
                Download P&L reports and transaction data in CSV format
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
